import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Camera, Check, Plus, X } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { HealthPassCardFront } from "../../components/card/HealthPassCardFront";
import { CardMark } from "../../components/card/CardChrome";
import {
  BLOOD_TYPES,
  COMMON_ALLERGIES,
  RELATIONSHIPS,
  basicsNumber,
  isDialable,
  mergeAllergies,
  minutesLeft,
  nextStage,
  progress,
  stepOf,
  type Stage,
} from "../../components/firstrun/firstRunLogic";
import { CountryAutocomplete } from "../../components/ui/CountryAutocomplete";
import apiClient from "../../services/api/apiClient";
import { uploadAvatar } from "../../services/api/avatar.service";
import {
  cardService,
  type CardDTO,
  type CardQR,
} from "../../services/api/cardService";
import { medicationService } from "../../services/api/medicationService";
import { createTravel } from "../../services/api/travels";
import { useAuthStore } from "../../stores/authStore";
import { useI18nStore } from "../../stores/i18nStore";

/**
 * First run (UX_CONCEPT.md §5.5). The card builds itself as the traveller
 * answers: three steps, one question at a time, each answer saved and on the
 * card at once. The QR code lands when the emergency basics are done.
 *
 * It replaces a five-slide carousel that described features instead of
 * setting any of them up, and whose "Plan first trip" opened a trip called
 * "create".
 */

interface ProfileOnRecord {
  allergies?: string[];
  emergencyContacts?: Array<{ name: string; phone: string }>;
}

async function saveProfile(fields: Record<string, unknown>): Promise<void> {
  await apiClient.put("/users/profile", fields);
}

const chipCls = (on: boolean) =>
  `inline-flex min-h-[44px] items-center gap-1.5 rounded-full border px-4 text-sm font-semibold transition-colors ${
    on
      ? "border-primary-600 bg-primary-600 text-white"
      : "border-slate-300 bg-white text-ink hover:border-primary-400"
  }`;

const inputCls =
  "mt-1.5 block min-h-[48px] w-full rounded-[14px] border border-slate-300 bg-white px-3.5 text-[15px] text-ink focus:border-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-600/20";

const labelCls = "block text-[13px] font-semibold text-ink";

const primaryBtn =
  "min-h-[52px] flex-[1.4] rounded-2xl bg-ink px-4 text-[15px] font-bold text-white disabled:opacity-50";
const secondaryBtn =
  "min-h-[52px] flex-1 rounded-2xl border border-slate-300 bg-white px-4 text-[15px] font-semibold text-ink";

export const FirstRunPage: React.FC = () => {
  const { t } = useI18nStore();
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: card } = useQuery<CardDTO>({
    queryKey: ["healthpass-card"],
    queryFn: () => cardService.getCard(),
  });
  const { data: onRecord } = useQuery<ProfileOnRecord>({
    queryKey: ["first-run-profile"],
    queryFn: async () =>
      (await apiClient.get("/users/profile")).data?.data?.user ?? {},
  });

  const [stage, setStage] = useState<Stage>("you");
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  const [qr, setQr] = useState<CardQR | null>(null);

  // Step 1
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [nationality, setNationality] = useState("");
  const [photoFailed, setPhotoFailed] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  // Step 2
  const [bloodType, setBloodType] = useState<string | null>(null);
  const [allergies, setAllergies] = useState<string[]>([]);
  const [otherAllergy, setOtherAllergy] = useState("");
  const [allergiesOnCard, setAllergiesOnCard] = useState<string[] | null>(null);
  const [meds, setMeds] = useState<string[]>([]);
  const [medDraft, setMedDraft] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [relationship, setRelationship] = useState<string>("Partner");
  const [phoneInvalid, setPhoneInvalid] = useState(false);

  // Step 3
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("");
  const [departure, setDeparture] = useState("");
  const [returnDate, setReturnDate] = useState("");
  const [tripId, setTripId] = useState<string | null>(null);

  // Prefill once from what is already on record.
  const prefilled = useRef(false);
  useEffect(() => {
    if (prefilled.current || !card) return;
    prefilled.current = true;
    setFirstName(card.holder?.firstName || user?.firstName || "");
    setLastName(card.holder?.lastName || user?.lastName || "");
    setDateOfBirth(card.holder?.dateOfBirth?.slice(0, 10) ?? "");
    setNationality(card.holder?.nationality ?? "");
    setBloodType(card.emergencySummary?.bloodType ?? null);
  }, [card, user]);

  const refreshCard = () =>
    queryClient.invalidateQueries({ queryKey: ["healthpass-card"] });

  /** Runs a save; moves on when it worked, says so when it did not. */
  const answer = async (save: () => Promise<void>) => {
    setSaving(true);
    setFailed(false);
    try {
      await save();
      refreshCard();
      setStage((s) => nextStage(s));
    } catch {
      setFailed(true);
    } finally {
      setSaving(false);
    }
  };

  // The emergency basics are done: the card gets its QR code.
  useEffect(() => {
    if (stage !== "trip" || qr) return;
    cardService
      .getQR()
      .then(setQr)
      .catch(() => {
        // The card page generates it on its next visit.
      });
  }, [stage, qr]);

  const onPhoto = async (file: File | undefined) => {
    if (!file) return;
    setPhotoFailed(false);
    try {
      await uploadAvatar(file);
      refreshCard();
    } catch {
      setPhotoFailed(true);
    }
  };

  // ── The card, as it stands ─────────────────────────────────────────────
  // An empty list on record is not an answer yet: nobody has said "none".
  const shownAllergies =
    allergiesOnCard ??
    (onRecord?.allergies?.length ? onRecord.allergies : null);
  const preview: CardDTO | null = card
    ? {
        ...card,
        holder: {
          ...card.holder,
          firstName,
          lastName,
          dateOfBirth: dateOfBirth || null,
          nationality: nationality || null,
        },
      }
    : null;
  const step = stepOf(stage);
  const chipFilled =
    "rounded-full bg-white px-2 py-0.5 text-[10px] font-bold text-primary-700";
  const chipEmpty =
    "rounded-full border border-dashed border-white/70 px-2 py-0.5 text-[10px] font-bold";
  const extras =
    step >= 2 ? (
      <>
        <span className={bloodType ? chipFilled : chipEmpty}>
          {bloodType
            ? t.frBloodChip.replace("{type}", bloodType)
            : t.frBloodChipEmpty}
        </span>
        <span className={shownAllergies ? chipFilled : chipEmpty}>
          {shownAllergies === null
            ? t.frAllergyChipEmpty
            : shownAllergies.length === 0
              ? t.frAllergyChipNone
              : shownAllergies.length === 1
                ? t.frAllergyChipOne
                : t.frAllergyChips.replace(
                    "{n}",
                    String(shownAllergies.length),
                  )}
        </span>
      </>
    ) : null;

  const [title, lead] =
    stage === "done"
      ? [t.frTitleDone, t.frLeadDone.replace("{city}", city)]
      : step === 1
        ? [t.frTitleYou, t.frLeadYou]
        : step === 2
          ? [t.frTitleBasics, t.frLeadBasics]
          : [t.frTitleTrip, t.frLeadTrip];

  const bars = progress(stage);
  const steps = [
    { n: 1, title: t.frStepYou, hint: t.frStepYouHint },
    { n: 2, title: t.frStepBasics, hint: t.frStepBasicsHint },
    { n: 3, title: t.frStepTrip, hint: t.frStepTripHint },
  ];
  const basics = basicsNumber(stage);

  // ── The current question ───────────────────────────────────────────────
  let question: React.ReactNode = null;
  if (stage === "you") {
    const ready = firstName.trim() && lastName.trim() && dateOfBirth;
    question = (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!ready) return;
          answer(() =>
            saveProfile({
              firstName: firstName.trim(),
              lastName: lastName.trim(),
              dateOfBirth,
              nationality: nationality.trim() || null,
            }),
          );
        }}
      >
        <h2 className="font-display text-[22px] font-bold leading-tight">
          {t.frYouQ}
        </h2>
        <div className="mt-4 grid grid-cols-2 gap-3">
          <label className={labelCls}>
            {t.frFirstName}
            <input
              className={inputCls}
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              autoComplete="given-name"
              required
            />
          </label>
          <label className={labelCls}>
            {t.frLastName}
            <input
              className={inputCls}
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              autoComplete="family-name"
              required
            />
          </label>
        </div>
        <label className={`${labelCls} mt-3`}>
          {t.frDateOfBirth}
          <input
            type="date"
            className={inputCls}
            value={dateOfBirth}
            max={new Date().toISOString().slice(0, 10)}
            onChange={(e) => setDateOfBirth(e.target.value)}
            autoComplete="bday"
            required
          />
        </label>
        <label className={`${labelCls} mt-3`}>
          {t.frNationality}
          <input
            className={inputCls}
            value={nationality}
            onChange={(e) => setNationality(e.target.value)}
            placeholder={t.frNationalityPlaceholder}
          />
        </label>
        <div className="mt-4 flex items-center gap-3">
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="inline-flex min-h-[44px] items-center gap-2 rounded-full border border-slate-300 bg-white px-4 text-sm font-semibold text-ink"
          >
            <Camera className="h-4 w-4" aria-hidden="true" />
            {card?.holder?.avatarUrl ? t.frChangePhoto : t.frAddPhoto}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => onPhoto(e.target.files?.[0])}
          />
          {photoFailed && (
            <span role="alert" className="text-sm text-travel-700">
              {t.frPhotoFailed}
            </span>
          )}
        </div>
        <div className="mt-5 flex">
          <button
            type="submit"
            className={primaryBtn}
            disabled={!ready || saving}
          >
            {t.frPutOnCard}
          </button>
        </div>
      </form>
    );
  } else if (stage === "blood") {
    question = (
      <div>
        <h2 className="font-display text-[22px] font-bold leading-tight">
          {t.frBloodQ}
        </h2>
        <p className="mt-1.5 text-sm text-slate-600">{t.frBloodHint}</p>
        <div className="mt-4 grid grid-cols-4 gap-2">
          {BLOOD_TYPES.map((type) => (
            <button
              key={type}
              type="button"
              aria-pressed={bloodType === type}
              onClick={() => setBloodType(type)}
              className={`${chipCls(bloodType === type)} justify-center px-0 font-mono`}
            >
              {type.replace("-", "−")}
            </button>
          ))}
        </div>
        <div className="mt-5 flex gap-2.5">
          <button
            type="button"
            className={secondaryBtn}
            onClick={() => setStage(nextStage(stage))}
          >
            {t.frBloodUnknown}
          </button>
          <button
            type="button"
            className={primaryBtn}
            disabled={!bloodType || saving}
            onClick={() => answer(() => saveProfile({ bloodType }))}
          >
            {t.frAddToCard}
          </button>
        </div>
      </div>
    );
  } else if (stage === "allergies") {
    const existing = onRecord?.allergies ?? [];
    const save = (list: string[]) =>
      answer(async () => {
        await saveProfile({ allergies: list });
        setAllergiesOnCard(list);
      });
    question = (
      <div>
        <h2 className="font-display text-[22px] font-bold leading-tight">
          {t.frAllergyQ}
        </h2>
        <p className="mt-1.5 text-sm text-slate-600">{t.frAllergyHint}</p>
        <div className="mt-4 flex flex-wrap gap-2">
          {COMMON_ALLERGIES.map((a) => {
            const on = allergies.includes(a);
            return (
              <button
                key={a}
                type="button"
                aria-pressed={on}
                onClick={() =>
                  setAllergies((prev) =>
                    on ? prev.filter((x) => x !== a) : [...prev, a],
                  )
                }
                className={chipCls(on)}
              >
                {a}
                {on && <Check className="h-4 w-4" aria-hidden="true" />}
              </button>
            );
          })}
        </div>
        {existing.length > 0 && (
          <p className="mt-3 text-sm text-slate-600">
            {t.frAlreadyOnCard}: {existing.join(", ")}
          </p>
        )}
        <label className={`${labelCls} mt-4`}>
          {t.frSomethingElse}
          <input
            className={inputCls}
            value={otherAllergy}
            onChange={(e) => setOtherAllergy(e.target.value)}
            placeholder={t.frAllergyPlaceholder}
          />
        </label>
        <div className="mt-5 flex gap-2.5">
          <button
            type="button"
            className={secondaryBtn}
            disabled={saving}
            onClick={() =>
              existing.length ? setStage(nextStage(stage)) : save([])
            }
          >
            {existing.length ? t.frNothingToAdd : t.frNoAllergies}
          </button>
          <button
            type="button"
            className={primaryBtn}
            disabled={saving || (!allergies.length && !otherAllergy.trim())}
            onClick={() =>
              save(mergeAllergies(existing, allergies, otherAllergy))
            }
          >
            {t.frAddToCard}
          </button>
        </div>
      </div>
    );
  } else if (stage === "meds") {
    const addDraft = () => {
      const name = medDraft.trim();
      if (name && !meds.includes(name)) setMeds((m) => [...m, name]);
      setMedDraft("");
    };
    question = (
      <div>
        <h2 className="font-display text-[22px] font-bold leading-tight">
          {t.frMedsQ}
        </h2>
        <p className="mt-1.5 text-sm text-slate-600">{t.frMedsHint}</p>
        <div className="mt-4 flex gap-2">
          <input
            aria-label={t.frMedsQ}
            className={`${inputCls} mt-0 flex-1`}
            value={medDraft}
            onChange={(e) => setMedDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addDraft();
              }
            }}
            placeholder={t.frMedsPlaceholder}
          />
          <button
            type="button"
            onClick={addDraft}
            disabled={!medDraft.trim()}
            className="inline-flex min-h-[48px] items-center gap-1 rounded-[14px] border border-slate-300 bg-white px-4 text-sm font-semibold text-ink disabled:opacity-50"
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            {t.frAdd}
          </button>
        </div>
        {meds.length > 0 && (
          <ul className="mt-3 flex flex-wrap gap-2">
            {meds.map((m) => (
              <li key={m}>
                <button
                  type="button"
                  onClick={() => setMeds((list) => list.filter((x) => x !== m))}
                  aria-label={`${t.frRemove} ${m}`}
                  className={chipCls(true)}
                >
                  {m}
                  <X className="h-4 w-4" aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className="mt-5 flex gap-2.5">
          <button
            type="button"
            className={secondaryBtn}
            disabled={saving}
            onClick={() => setStage(nextStage(stage))}
          >
            {t.frNoMeds}
          </button>
          <button
            type="button"
            className={primaryBtn}
            disabled={saving || (!meds.length && !medDraft.trim())}
            onClick={() => {
              const all = [...meds, medDraft.trim()].filter(
                (m, i, list) => m && list.indexOf(m) === i,
              );
              const today = new Date().toISOString().slice(0, 10);
              answer(async () => {
                for (const name of all) {
                  await medicationService.createMedication({
                    name,
                    startDate: today,
                  });
                }
                setMeds([]);
                setMedDraft("");
              });
            }}
          >
            {t.frAddToCard}
          </button>
        </div>
      </div>
    );
  } else if (stage === "contact") {
    const existing = onRecord?.emergencyContacts ?? [];
    question = (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!isDialable(contactPhone)) {
            setPhoneInvalid(true);
            return;
          }
          setPhoneInvalid(false);
          answer(() =>
            saveProfile({
              emergencyContacts: [
                {
                  name: contactName.trim(),
                  phone: contactPhone.trim(),
                  relationship,
                  isPrimary: true,
                },
                ...existing,
              ].slice(0, 5),
            }),
          );
        }}
      >
        <h2 className="font-display text-[22px] font-bold leading-tight">
          {t.frContactQ}
        </h2>
        <p className="mt-1.5 text-sm text-slate-600">{t.frContactHint}</p>
        <label className={`${labelCls} mt-4`}>
          {t.frContactName}
          <input
            className={inputCls}
            value={contactName}
            onChange={(e) => setContactName(e.target.value)}
            required
          />
        </label>
        <label className={`${labelCls} mt-3`}>
          {t.frContactPhone}
          <input
            type="tel"
            className={inputCls}
            value={contactPhone}
            onChange={(e) => setContactPhone(e.target.value)}
            placeholder="+1 416 555 0100"
            aria-invalid={phoneInvalid}
            required
          />
        </label>
        {phoneInvalid && (
          <p role="alert" className="mt-1.5 text-sm text-travel-700">
            {t.frContactPhoneInvalid}
          </p>
        )}
        <div
          className="mt-4 flex flex-wrap gap-2"
          role="group"
          aria-label={t.frRelationship}
        >
          {RELATIONSHIPS.map((r) => (
            <button
              key={r}
              type="button"
              aria-pressed={relationship === r}
              onClick={() => setRelationship(r)}
              className={chipCls(relationship === r)}
            >
              {t[`frRel${r}` as const]}
            </button>
          ))}
        </div>
        <div className="mt-5 flex gap-2.5">
          <button
            type="button"
            className={secondaryBtn}
            disabled={saving}
            onClick={() => setStage(nextStage(stage))}
          >
            {t.frSkip}
          </button>
          <button
            type="submit"
            className={primaryBtn}
            disabled={saving || !contactName.trim() || !contactPhone.trim()}
          >
            {t.frAddToCard}
          </button>
        </div>
      </form>
    );
  } else if (stage === "trip") {
    const ready = city.trim() && country && departure && returnDate;
    question = (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!ready) return;
          answer(async () => {
            const trip = await createTravel({
              tripTitle: city.trim(),
              destination: city.trim(),
              country,
              departureDate: departure,
              returnDate,
            });
            setTripId(trip._id);
          });
        }}
      >
        <h2 className="font-display text-[22px] font-bold leading-tight">
          {t.frTripQ}
        </h2>
        <div className="mt-4 grid grid-cols-2 gap-3 [&>*:nth-child(-n+2)]:col-span-2">
          <label className={labelCls}>
            {t.frTripCity}
            <input
              className={inputCls}
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="Bangkok"
              required
            />
          </label>
          <div className={labelCls}>
            {t.frTripCountry}
            <div className="mt-1.5">
              <CountryAutocomplete value={country} onChange={setCountry} />
            </div>
          </div>
          <label className={labelCls}>
            {t.frTripDeparture}
            <input
              type="date"
              className={inputCls}
              value={departure}
              min={new Date().toISOString().slice(0, 10)}
              onChange={(e) => {
                setDeparture(e.target.value);
                if (!returnDate || returnDate < e.target.value)
                  setReturnDate(e.target.value);
              }}
              required
            />
          </label>
          <label className={labelCls}>
            {t.frTripReturn}
            <input
              type="date"
              className={inputCls}
              value={returnDate}
              min={departure || undefined}
              onChange={(e) => setReturnDate(e.target.value)}
              required
            />
          </label>
        </div>
        <div className="mt-5 flex gap-2.5">
          <button
            type="button"
            className={secondaryBtn}
            onClick={() => navigate("/dashboard")}
          >
            {t.frTripLater}
          </button>
          <button
            type="submit"
            className={primaryBtn}
            disabled={!ready || saving}
          >
            {t.frTripPlan}
          </button>
        </div>
      </form>
    );
  } else {
    question = (
      <div className="flex flex-col gap-2.5 sm:flex-row">
        {tripId && (
          <Link
            to={`/traveler/travel/${tripId}/readiness`}
            className="flex min-h-[52px] flex-1 items-center justify-center rounded-2xl bg-travel-500 px-4 text-[15px] font-bold text-ink"
          >
            {t.frOpenChecklist.replace("{city}", city)}
          </Link>
        )}
        <Link
          to="/dashboard"
          className="flex min-h-[52px] flex-1 items-center justify-center rounded-2xl border border-slate-300 bg-white px-4 text-[15px] font-semibold text-ink"
        >
          {t.frGoHome}
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ground pb-12 text-ink">
      <div className="mx-auto max-w-[480px] px-4 pt-[max(env(safe-area-inset-top),20px)]">
        <div className="flex items-center justify-between pt-4">
          <div className="flex items-center gap-2 text-primary-600">
            <CardMark className="h-[22px] w-[22px]" />
            <span className="font-display text-base font-bold tracking-[0.04em] text-ink">
              HEALTH PASS
            </span>
          </div>
          <span className="text-[13px] font-semibold text-muted-foreground">
            {t.frStepOf.replace("{n}", String(step))}
          </span>
        </div>
        <div className="mt-4 grid grid-cols-3 gap-1.5" aria-hidden="true">
          {bars.map((fill, i) => (
            <div
              key={i}
              className="h-1.5 overflow-hidden rounded-full bg-slate-200"
            >
              <div
                className="h-full rounded-full bg-primary-600 transition-[width] duration-500 motion-reduce:transition-none"
                style={{ width: `${Math.round(fill * 100)}%` }}
              />
            </div>
          ))}
        </div>

        <h1 className="mt-7 font-display text-[32px] font-extrabold leading-[1.05] tracking-[-0.01em]">
          {title}
        </h1>
        <p className="mt-2.5 text-[15px] leading-normal text-slate-600">
          {lead}
        </p>

        <div className="mt-6 flex justify-center">
          <HealthPassCardFront
            cardData={preview}
            qrData={qr}
            qrPlaceholder={t.frQrAfter}
            extras={extras}
          />
        </div>

        <section
          aria-label={
            basics
              ? t.frBasicsLabel.replace("{n}", String(basics))
              : steps[step - 1]!.title
          }
          className="mt-7 rounded-[22px] bg-white p-5"
        >
          {basics && (
            <div className="mb-2 text-[11px] font-bold tracking-[0.14em] text-travel-700">
              {t.frBasicsLabel.replace("{n}", String(basics))}
            </div>
          )}
          {question}
          {failed && (
            <p
              role="alert"
              className="mt-3 text-sm font-semibold text-travel-700"
            >
              {t.frSaveFailed}
            </p>
          )}
        </section>

        {stage !== "done" && (
          <ol aria-label={t.frYourSteps} className="mt-5 flex flex-col gap-2.5">
            {steps.map((s) => {
              const done = s.n < step;
              const current = s.n === step;
              return (
                <li
                  key={s.n}
                  aria-current={current ? "step" : undefined}
                  className={`flex items-center gap-3.5 rounded-[18px] bg-white px-4 py-3.5 ${current ? "ring-2 ring-primary-600" : ""}`}
                >
                  <span
                    className={`flex h-8 w-8 flex-none items-center justify-center rounded-full font-bold ${
                      done
                        ? "bg-health-700 text-white"
                        : current
                          ? "bg-primary-600 text-white"
                          : "bg-slate-100 text-slate-600"
                    }`}
                  >
                    {done ? (
                      <Check
                        className="h-[18px] w-[18px]"
                        aria-label={t.frDone}
                      />
                    ) : (
                      s.n
                    )}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-[15px] font-bold">{s.title}</div>
                    <div className="text-[13px] text-muted-foreground">
                      {s.hint}
                    </div>
                  </div>
                  {current && (
                    <span className="text-xs font-bold text-primary-700">
                      {t.frMinLeft.replace("{n}", String(minutesLeft(stage)))}
                    </span>
                  )}
                </li>
              );
            })}
          </ol>
        )}

        <p className="mx-3 mt-5 text-center text-xs leading-normal text-muted-foreground">
          {t.frPrivacy}
        </p>
      </div>
    </div>
  );
};

export default FirstRunPage;
